import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Plus, Vote, ArrowLeft, Clock } from "lucide-react";
import api, { getBoardroomToken, clearBoardroomToken } from "../utils/qccaApi";

export default function QCCAVoteList() {
  const nav = useNavigate();
  const [votes, setVotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getBoardroomToken()) { nav("/qcca/boardroom/login"); return; }
    api.get("/qcca/votes")
      .then((r) => setVotes(r.data.votes || []))
      .catch((e) => {
        if (e?.response?.status === 401) { clearBoardroomToken(); nav("/qcca/boardroom/login"); }
        else toast.error("Failed to load votes.");
      })
      .finally(() => setLoading(false));
  }, [nav]);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/qcca/boardroom" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 hover:text-[#C5A52A] mb-6">
          <ArrowLeft className="w-3 h-3" /> Boardroom
        </Link>

        <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Secure Vote-by-Email</div>
            <h1 className="mt-3 font-headline text-4xl text-white leading-tight">Your <span className="italic text-[#C5A52A]">votes.</span></h1>
          </div>
          <Link
            to="/qcca/boardroom/votes/new"
            className="inline-flex items-center gap-2 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-mono text-xs uppercase tracking-[0.22em] px-5 py-3 transition-colors"
            data-testid="qcca-vote-new-btn"
          >
            <Plus className="w-4 h-4" /> Start a new vote
          </Link>
        </div>

        {loading ? (
          <div className="text-gray-500 font-mono text-sm">Loading…</div>
        ) : votes.length === 0 ? (
          <div className="border border-dashed border-[#1a1a1a] bg-[#0a0a0a] p-12 text-center">
            <Vote className="w-10 h-10 text-[#C5A52A] mx-auto" />
            <h3 className="mt-4 font-headline text-2xl text-white">No votes yet.</h3>
            <p className="mt-2 text-gray-400 text-sm max-w-md mx-auto">
              Your first vote is included. Upload a CSV of unit owners, draft your resolution, and we'll send each owner a unique tokenized ballot.
            </p>
            <Link to="/qcca/boardroom/votes/new" className="inline-flex items-center gap-2 mt-6 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-mono text-xs uppercase tracking-[0.22em] px-5 py-3 transition-colors">
              <Plus className="w-4 h-4" /> Start your first vote
            </Link>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="qcca-vote-list">
            {votes.map((v) => {
              const pct = v.total_voters ? Math.round((v.votes_cast / v.total_voters) * 100) : 0;
              return (
                <Link
                  key={v.id}
                  to={`/qcca/boardroom/votes/${v.id}`}
                  className="block group bg-[#0a0a0a] border border-[#1a1a1a] hover:border-[#C5A52A]/60 p-6 transition-colors"
                  data-testid={`qcca-vote-row-${v.id}`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-headline text-xl text-white leading-tight group-hover:text-[#C5A52A] transition-colors">
                        {v.title}
                      </h3>
                      <p className="mt-2 text-gray-400 text-sm line-clamp-2">{v.description}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">Turnout</div>
                      <div className="font-headline text-3xl text-[#C5A52A]">{pct}%</div>
                      <div className="font-mono text-[10px] text-gray-500 mt-0.5">{v.votes_cast}/{v.total_voters}</div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">
                    <Clock className="w-3 h-3 text-[#C5A52A]" />
                    <span>Deadline {new Date(v.deadline_iso).toLocaleString()}</span>
                    <span className={`border px-2 py-0.5 ${v.status === "open" ? "border-[#C5A52A]/60 text-[#C5A52A]" : "border-[#1a1a1a] text-gray-500"}`}>
                      {v.status}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
